/**
 * What the in-app PDF viewer says when a preview cannot be shown.
 *
 * The viewer (react-pdf) fetches the file itself, so a failed preview never passes through the
 * api client and never reaches the global toast. It used to show one fixed line, "Couldn't load
 * this document.", for every failure — a renderer that shed the request, a file missing from
 * storage, a network drop — so the honest answers the server now gives were never seen here.
 *
 * pdf.js reports an HTTP failure as a `ResponseException` carrying `status` (and `missing` for a
 * 404). Those two facts are enough to say the same thing the rest of the app says.
 */

/** Mirrors the backend's `RETRY_MESSAGE` and the global 5xx toast — one sentence, everywhere. */
export const PREVIEW_RETRY_MESSAGE = 'Something went wrong on our end. Please try again in a moment.';
export const PREVIEW_MISSING_MESSAGE = "We couldn't find that file. It may have been removed from storage.";
export const PREVIEW_GENERIC_MESSAGE = "Couldn't load this document. Please try again.";
/** Word-for-word the server's own 415 detail and the viewer's `unsupported` branch — the same
 *  refusal should read the same however the person arrives at it. */
export const PREVIEW_UNSUPPORTED_MESSAGE =
  "This file type can't be shown here. Download it to open it.";

function statusOf(err: unknown): number | null {
  if (typeof err !== 'object' || err === null) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

function isMissing(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  return (err as { missing?: unknown }).missing === true;
}

/** The sentence to show for a preview that failed to load. */
export function previewFailureText(err: unknown): string {
  const status = statusOf(err);
  if (status === 404 || isMissing(err)) return PREVIEW_MISSING_MESSAGE;
  // 415 means the server WILL NOT render this type inline — a docx it could not convert to
  // PDF, say. Retrying cannot change that, so the generic "Please try again" was an
  // instruction to do the one thing guaranteed not to work. Say what actually helps.
  if (status === 415) return PREVIEW_UNSUPPORTED_MESSAGE;
  if (status !== null && status >= 500) return PREVIEW_RETRY_MESSAGE;
  return PREVIEW_GENERIC_MESSAGE;
}
